import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { CourseEditTemplate } from "@shared/models/analytics/course"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import Patch from "@shared/models/patch-updater"
import { find, get } from "lodash-es"
import type { Narrative } from "../../create-narrative"

interface Payload {
  courseId: string
  rotationId: string
  semesterId: string
  dateId: string
  cardStackId: string | null
  currentTemplateId: string | null
  planbookId: string
}

export default {
  name: "COURSE_ROTATION_SET_TEMPLATE_ID",

  params: {
    courseId: "string",
    rotationId: "string",
    semesterId: "string",
    dateId: "string",
    cardStackId: { type: ["string", "null"] },
    currentTemplateId: "string",
  },

  patches(payload: Payload) {
    return [
      Patch.create("course", payload.courseId, "attributes.calendar.dates", {
        id: payload.dateId,
      })
        .set("embedded", "attributes.cardStackId", payload.cardStackId)
        .inc("attributes.calendar.version", 1).patch,
    ]
  },

  undoPatches(payload: Payload) {
    return [
      Patch.create("course", payload.courseId, "attributes.calendar.dates", {
        id: payload.dateId,
      })
        .set("embedded", "attributes.cardStackId", payload.currentTemplateId)
        .inc("attributes.calendar.version", 1).patch,
    ]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let course = findFn("course", payload.courseId)
    let template = findFn("card-stack", payload.cardStackId ?? "")
    let planbook = findFn("planbook", payload.planbookId)
    let rotationCalendar = findFn(
      "rotation-calendar",
      planbook.relationships.rotationCalendar.data.id
    )
    let models = {
      template: template,
      course: course,
      planbook: planbook,
      rotationCalendar,
    }

    let templateTitle = get(models, "template.attributes.title")
    let rotation = find(get(models, "rotationCalendar.attributes.rotations"), {
      id: payload.rotationId,
    })
    let title = get(rotation, "title")
    let newEvent: CourseEditTemplate = {
      ...createBlankEvent(),
      root_id: payload.planbookId,
      root_type: "planbook",
      date_plan_scheduled_for: null,

      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

      primary_object_type: "course",
      primary_object_id: payload.courseId,
      primary_object_specifier: "template_assignment",
      primary_object_id_and_ancestor_ids: [payload.planbookId, payload.courseId],

      adverbial_verb: "editing",

      adverbial_object_type: "rotation",
      // Note: This might be a weekday instead of an ObjectId.
      adverbial_object_id: payload.rotationId,
      adverbial_object_specifier: "specific",
      adverbial_object_id_and_ancestor_ids: [payload.courseId, payload.planbookId],
    }
    return {
      title: `Changed the template`,
      titleForAnalytics: "Course - Change Template for Rotation",
      description: `Change the template to "${templateTitle}" for all lessons that happen on ${title}`,
      context: {
        templateTitle: get(models, "template.attributes.title"),
        courseTitle: get(models, "course.attributes.title"),
        planbookTitle: get(models, "planbook.attributes.title"),
        courseId: get(models, "course.id"),
        planbookId: get(models, "planbook.id"),
      },
      activity: {
        actor: `user-${userId}`,
        verb: "edited",
        object: `course-${models.course.id}`,
        to: [
          `course-edits:course-${models.course.id}`,
          `planbook-edits:planbook-${models.planbook.id}`,
        ],
      },
      newAnalyticsEvents: [newEvent],
    }
  },
}
